/**
 * Shared utilities for sanitizing note/remark HTML content.
 *
 * Handles two note formats:
 * - Rich-text HTML (from the Quill editor) — strip SOAP headers, pass through
 * - Plain text (legacy notes) — HTML-escape first, then strip SOAP headers
 *
 * SOAP headers like <S>, <O>, <A/P> are legacy formatting markers that browsers
 * interpret as HTML tags. They are stripped so they don't break rendering.
 */

export const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'div',
    'span',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'strike',
    'del',
    'mark',
    'small',
    'sub',
    'sup',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'a',
    'hr',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  ALLOW_DATA_ATTR: false,
}

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const stripSoapHeaders = (text) => {
  if (!text) return text
  return text
    .replace(/<S>/g, '')
    .replace(/<O>/g, '')
    .replace(/<A\/P>/g, '')
    .replace(/<A>/g, '')
    .replace(/<P>/g, '')
    .replace(/<\/S>/g, '')
    .replace(/<\/O>/g, '')
    .replace(/<\/A>/g, '')
    .replace(/<\/P>/g, '')
    .replace(/&lt;S&gt;/g, '')
    .replace(/&lt;O&gt;/g, '')
    .replace(/&lt;A\/P&gt;/g, '')
    .replace(/&lt;A&gt;/g, '')
    .replace(/&lt;P&gt;/g, '')
    .replace(/&lt;\/S&gt;/g, '')
    .replace(/&lt;\/O&gt;/g, '')
    .replace(/&lt;\/A&gt;/g, '')
    .replace(/&lt;\/P&gt;/g, '')
}

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const sanitizeNoteHtml = (html) => {
  if (!html) return html
  const isRichText = /<(p|div|br|h[1-6]|ul|ol|li|blockquote|pre)\b/i.test(html)
  if (isRichText) return stripSoapHeaders(html)
  const escaped = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return stripSoapHeaders(escaped)
}

// A contentEditable element's .innerHTML serializes repeated/trailing spaces as
// literal &nbsp; entity text. Normalize that (plus numeric entity forms and a raw
// NBSP character from paste) back to a plain space before persisting. QMD-3694.
const NON_BREAKING_SPACE_ENTITY_PATTERN = /&nbsp;|&#160;|&#x0*a0;/gi

export const normalizeNonBreakingSpaces = (text: string) => {
  if (!text) return text
  return text.replace(NON_BREAKING_SPACE_ENTITY_PATTERN, ' ').replace(/\u00A0/g, ' ')
}
